/**
 * usePaywallProducts Hook
 *
 * Provides paywall products data loading and CRUD operations.
 *
 * @module paywall-products/hooks/usePaywallProducts
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { PaywallProductService } from '@modules/firebase-core/services/PaywallProductService';
import type { PaywallProduct } from '@segmently/schema-models/payments';

/**
 * Options for usePaywallProducts hook
 */
export interface UsePaywallProductsOptions {
  /** Project ID */
  projectId: string;

  /** Auto-load on mount */
  autoLoad?: boolean;

  /** Filter by type */
  type?: 'one_time' | 'subscription';
}

/**
 * Return type for usePaywallProducts hook
 */
export interface UsePaywallProductsResult {
  /** List of paywall products */
  products: PaywallProduct[];

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Refresh products list */
  refresh: () => Promise<void>;

  /** Create new product */
  createProduct: (
    data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>
  ) => Promise<PaywallProduct>;

  /** Update product */
  updateProduct: (
    productId: string,
    data: Partial<Omit<PaywallProduct, 'id' | 'createdAt'>>
  ) => Promise<void>;

  /** Delete product */
  deleteProduct: (productId: string) => Promise<void>;

  /** Get single product by ID */
  getProduct: (productId: string) => Promise<PaywallProduct | null>;
}

/**
 * Hook for managing paywall products.
 *
 * @example
 * ```tsx
 * const { products, loading, createProduct, deleteProduct } = usePaywallProducts({
 *   projectId: 'proj_123',
 * });
 *
 * // Create new product
 * await createProduct({
 *   name: 'Premium Plan',
 *   type: 'subscription',
 *   stripeProductId: 'prod_xxx',
 *   stripePriceId: 'price_xxx',
 * });
 *
 * // Delete product
 * await deleteProduct('product_id');
 * ```
 */
export function usePaywallProducts(
  options: UsePaywallProductsOptions
): UsePaywallProductsResult {
  const { projectId, autoLoad = true, type } = options;

  const [products, setProducts] = useState<PaywallProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(() => new PaywallProductService(), []);

  /**
   * Load products from Firestore
   */
  const loadProducts = useCallback(async () => {
    if (!projectId) {
      setProducts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await service.list(projectId, { type });
      setProducts(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load products';
      setError(message);
      console.error('Failed to load paywall products:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, type, service]);

  /**
   * Create new product
   */
  const createProduct = useCallback(
    async (
      data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>
    ): Promise<PaywallProduct> => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      try {
        const product = await service.create(projectId, data);
        // Refresh list to include new product
        await loadProducts();
        return product;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create product';
        setError(message);
        throw err;
      }
    },
    [projectId, service, loadProducts]
  );

  /**
   * Update existing product
   */
  const updateProduct = useCallback(
    async (
      productId: string,
      data: Partial<Omit<PaywallProduct, 'id' | 'createdAt'>>
    ): Promise<void> => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      try {
        await service.update(projectId, productId, data);
        // Refresh list to reflect changes
        await loadProducts();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update product';
        setError(message);
        throw err;
      }
    },
    [projectId, service, loadProducts]
  );

  /**
   * Delete product
   */
  const deleteProduct = useCallback(
    async (productId: string): Promise<void> => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      try {
        await service.delete(projectId, productId);
        // Refresh list to remove deleted product
        await loadProducts();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete product';
        setError(message);
        throw err;
      }
    },
    [projectId, service, loadProducts]
  );

  /**
   * Get single product by ID
   */
  const getProduct = useCallback(
    async (productId: string): Promise<PaywallProduct | null> => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      try {
        return await service.get(projectId, productId);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to get product';
        setError(message);
        throw err;
      }
    },
    [projectId, service]
  );

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && projectId) {
      loadProducts();
    }
  }, [autoLoad, projectId, loadProducts]);

  return {
    products,
    loading,
    error,
    refresh: loadProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getProduct,
  };
}

export default usePaywallProducts;
